import React, { useState, useEffect } from 'react';
import { Box, Typography, FormControl, Select, MenuItem, TextField, Chip } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';

import { projectsApi } from '../../../../../../../../services/api';
import LoadingSpinner from '../../../../../../../common/LoadingSpinner';
import { Field, Label } from '../../../../../../../common/Modal';
import { EmployeeAvatar } from '../../../../../../../common/EmployeeAvatar';
import ProjectSearch from '../../../../../../../common/ProjectSearch';
import { toPickerValue } from '../../../../../../../../utils/dateOnly';

const TransferDetails = ({
  selectedEmployee,
  selectedProject,
  setSelectedProject,
  endDate,
  setEndDate,
  projectId,
  currentEngagementType,
  engagementTypes
}) => {
  // Handle projectId for pre-selecting a project
  useEffect(() => {
    // Fetch project by ID if projectId is provided
    if (projectId) {
      fetchProjectById(projectId);
    }
  }, [projectId]);

  const fetchProjectById = async (projectId) => {
    try {
      const project = await projectsApi.getProjectById(projectId);
      if (project) {
        // Create project object with purpose information
        const projectWithPurpose = {
          id: project.id,
          name: project.name,
          projectPurpose: project.projectPurpose ? {
            id: project.projectPurpose.id,
            code: project.projectPurpose.code,
            name: project.projectPurpose.name
          } : null
        };
        
        setSelectedProject(projectWithPurpose);
      }
    } catch (error) {
      console.error('Error fetching project by ID:', error);
    }
  };

  const handleProjectChange = (project) => {
    setSelectedProject(project);
  };

  return (
    <>
      <Field>
          <Typography variant="subtitle1" gutterBottom>
            Individual
          </Typography>
        {selectedEmployee ? (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            p: 2,
            border: '1px solid #E2E8F0',
            borderRadius: '4px',
            backgroundColor: '#F8FAFC'
          }}>
            <EmployeeAvatar employee={selectedEmployee} size={32} />
            <Box>
              <Typography sx={{ fontWeight: 500 }}>{selectedEmployee.fullName}</Typography>
              <Typography variant="caption" color="textSecondary">
                {selectedEmployee.email}
              </Typography>
            </Box>
          </Box>
        ) : (
          <LoadingSpinner size={24} />
        )}
      </Field>

      <Field>
        <Typography variant="subtitle1" gutterBottom>
          Current Project
        </Typography>
        {projectId ? (
          selectedProject ? (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              gap: 1,
              p: 2,
              border: '1px solid #E2E8F0',
              borderRadius: '4px',
              backgroundColor: '#F8FAFC'
            }}>
              <Typography sx={{ fontWeight: 500 }}>{selectedProject.name}</Typography>
              {selectedProject.projectPurpose && (
                <Chip 
                  label={selectedProject.projectPurpose.name} 
                  size="small" 
                  sx={{ 
                    height: 20, 
                    fontSize: '0.7rem',
                    backgroundColor: '#e3f2fd',
                    color: '#1565c0'
                  }}
                />
              )}
            </Box>
          ) : (
            <LoadingSpinner size={24} />
          )
        ) : (
          <ProjectSearch
            value={selectedProject}
            onChange={handleProjectChange}
            employeeId={selectedEmployee?.id}
            placeholder="Search source project..."
            width={400}
            sx={{ width: '100%' }}
            showAssignmentIndicators={true}
          />
        )}
      </Field>

      <Field>
        <Typography variant="subtitle1" gutterBottom>
          Current Engagement Type
        </Typography>
        <FormControl fullWidth>
          <Select
            value={currentEngagementType?.id || ''}
            displayEmpty
            disabled
          >
            <MenuItem value={currentEngagementType?.id || ''}>
              {currentEngagementType?.name || 'No engagement type'}
            </MenuItem>
          </Select>
        </FormControl>
      </Field>

      <Field>
        <Typography variant="subtitle1" gutterBottom>
          Project End Date
        </Typography>
        <DatePicker
          value={endDate}
          onChange={(newValue) => {
            if (newValue && typeof newValue.isValid === 'function' && newValue.isValid()) {
              setEndDate(newValue);
            } else if (newValue === null) {
              setEndDate(null);
            }
          }}
          minDate={toPickerValue(selectedEmployee?.projectStartDate)}
          format="DD/MM/YYYY"
          slotProps={{
            textField: {
              fullWidth: true,
              placeholder: "When will the individual end on this project?",
              size: "medium",
              required: true,
              inputProps: {
                placeholder: 'DD/MM/YYYY'
              }
            }
          }}
        />
      </Field>
    </>
  );
};

export default TransferDetails; 